import request from '@/utils/request'
import { authState, hasPermission } from '@/utils/auth'
/** 车库子节点分页大小 */
export const GARAGE_CHILD_PAGE_SIZE = 500
export const GARAGE_NODE_LOAD_MORE = 'GARAGE_LOAD_MORE'

export function isGarageGroupNode(node) {
  if (!node || typeof node !== 'object') return false
  if (node.type === GARAGE_NODE_LOAD_MORE) return false
  const t = node.type != null ? String(node.type).toUpperCase() : ''
  if (t === 'GROUP') return true
  if (t === 'VEHICLE') return false
  const id = node.id != null ? String(node.id) : ''
  if (id.startsWith('v-')) return false
  return true
}

export function isUngroupedNode(node) {
  if (!node || typeof node !== 'object') return false
  const text = String(node.teamName || node.name || node.title || node.label || '').trim()
  return text.startsWith('未分组')
}

export function resolveGarageGroupExpectedChildTotal(raw) {
  if (!raw || typeof raw !== 'object') return null
  const n = raw.vehicleCount != null ? Number(raw.vehicleCount) : NaN
  if (!Number.isFinite(n) || n < 0) return null
  return Math.floor(n)
}

export function shouldShowGarageLoadMore(expectedTotal, lastPageLen, cumulativeLoadedCount) {
  if (lastPageLen <= 0) return false
  if (expectedTotal != null && Number.isFinite(expectedTotal) && expectedTotal >= 0) {
    return cumulativeLoadedCount < expectedTotal
  }
  return lastPageLen >= GARAGE_CHILD_PAGE_SIZE
}

export function createGarageLoadMoreNode(parentId, nextPage) {
  const pid = String(parentId)
  const p = Number(nextPage)
  return {
    id: `__garage_more__:${pid}:${p}`,
    label: '下一页',
    type: GARAGE_NODE_LOAD_MORE,
    garageLoadMoreParentId: pid,
    garageChildrenNextPage: p,
    isLeaf: true,
    children: []
  }
}

export function buildGroupLabel(name, raw) {
  const vc = raw && raw.vehicleCount != null ? Number(raw.vehicleCount) : null
  const tvc = raw && raw.totalVehicleCount != null ? Number(raw.totalVehicleCount) : null
  if (vc != null && tvc != null && Number.isFinite(vc) && Number.isFinite(tvc)) {
    return `${name} [${vc}/${tvc}]`
  }
  const count = tvc != null && Number.isFinite(tvc) ? tvc : vc != null && Number.isFinite(vc) ? vc : null
  if (count != null) return `${name} [${count}]`
  return name
}

export function resolveGarageVehicleColor(node) {
  const plateColorRaw = node && node.vehiclePlateColor
  if (plateColorRaw != null && String(plateColorRaw).trim() !== '') {
    const s = String(plateColorRaw).trim()
    if (['1', '2', '3'].includes(s)) return s
  }
  const plate = node && node.vehicleNo != null ? String(node.vehicleNo).trim() : ''
  if (plate.length === 8) return '3'
  return '2'
}

export function findGarageNodeById(nodes, id) {
  if (!Array.isArray(nodes) || id == null) return null
  for (const n of nodes) {
    if (!n || typeof n !== 'object') continue
    if (String(n.id) === String(id)) return n
    const hit = findGarageNodeById(n.children || [], id)
    if (hit) return hit
  }
  return null
}

export function collectGarageVehicleNodes(nodes) {
  if (!Array.isArray(nodes)) return []
  const out = []
  for (const n of nodes) {
    if (!n || typeof n !== 'object' || n.type === GARAGE_NODE_LOAD_MORE) continue
    if (n.vehicleNo != null) out.push(n)
    if (Array.isArray(n.children) && n.children.length) {
      out.push(...collectGarageVehicleNodes(n.children))
    }
  }
  return out
}

export function sortGarageNodesWithUngroupedFirst(nodes, isUngrouped = isUngroupedNode) {
  if (!Array.isArray(nodes)) return []
  const withIndex = nodes.map((n, idx) => {
    const next = n && typeof n === 'object' ? { ...n } : n
    if (next && Array.isArray(next.children) && next.children.length) {
      next.children = sortGarageNodesWithUngroupedFirst(next.children, isUngrouped)
    }
    return { idx, node: next }
  })
  withIndex.sort((a, b) => {
    const aUngrouped = isUngrouped(a.node)
    const bUngrouped = isUngrouped(b.node)
    if (aUngrouped !== bUngrouped) return aUngrouped ? -1 : 1
    return a.idx - b.idx
  })
  return withIndex.map((x) => x.node)
}

/** 模糊匹配：车牌、别名、分组名 */
export function garageNodeMatchesQuery(node, query) {
  const q = query != null ? String(query).trim().toUpperCase() : ''
  if (!q) return true
  if (!node || typeof node !== 'object') return false
  if (node.type === GARAGE_NODE_LOAD_MORE) return true
  const plate = node.vehicleNo != null ? String(node.vehicleNo).toUpperCase() : ''
  const alias = node.aliasName != null ? String(node.aliasName).toUpperCase() : ''
  const name = String(node.teamName || node.name || node.title || '').toUpperCase()
  if (plate && plate.includes(q)) return true
  if (alias && alias.includes(q)) return true
  if (name && name.includes(q)) return true
  return false
}

/** 按关键词过滤树：保留匹配节点及其祖先 */
export function filterGarageTreeByQuery(nodes, query) {
  const q = query != null ? String(query).trim() : ''
  if (!q || !Array.isArray(nodes)) return nodes || []

  const walk = (list) => {
    const out = []
    for (const n of list) {
      if (!n || typeof n !== 'object') continue
      if (n.type === GARAGE_NODE_LOAD_MORE) {
        out.push(n)
        continue
      }
      const children = Array.isArray(n.children) ? walk(n.children) : []
      const selfMatch = garageNodeMatchesQuery(n, q)
      if (selfMatch || children.length) {
        out.push({ ...n, children: selfMatch && isGarageGroupNode(n) ? n.children || [] : children })
      }
    }
    return out
  }
  return walk(nodes)
}

/** el-tree 的 default-expand-all 仅在首次渲染生效；模糊搜索时需手动展开匹配分支 */
export function applyElTreeExpandAll(treeRef, expand) {
  const tree = treeRef
  if (!tree || !tree.store || typeof tree.store._getAllNodes !== 'function') return
  const nodes = tree.store._getAllNodes()
  nodes.forEach((node) => {
    if (node.childNodes && node.childNodes.length) {
      node.expanded = !!expand
    }
  })
}

export function clearGarageTreeFirstLevelCache() {}

/** 本系统返回完整扁平树，转换后沿用源车库的分组与牌色节点交互。 */
export async function loadNormalVehicleGarageTree(query = '') {
  const actor = authState.user
  if (!actor || !hasPermission('vehicle:list')) return []
  const response = await request.get('/webserver/waybill/vehicle/tree', { params: { keyword: query } })
  if (authState.user !== actor || !hasPermission('vehicle:list')) return []
  if (!Array.isArray(response.data)) throw new Error('车辆分组响应格式错误')
  const byId = new Map()
  response.data.forEach(row => {
    const vehicle = row.nodeType === 'VEHICLE'
    const name = vehicle ? row.vehicleNo : row.label
    byId.set(row.nodeId, {
      ...row,
      id: vehicle ? `v-${row.vehicleNo}_${row.plateColor}` : row.nodeId,
      type: vehicle ? 'VEHICLE' : 'GROUP',
      vehiclePlateColor: vehicle ? String(row.plateColor) : undefined,
      teamName: vehicle ? undefined : name,
      displayName: name,
      label: name,
      children: [],
      hasChildren: false,
      isLeaf: vehicle
    })
  })
  const roots = []
  response.data.forEach(row => {
    const node = byId.get(row.nodeId)
    const parent = byId.get(row.parentNodeId)
    if (parent) parent.children.push(node)
    else roots.push(node)
  })
  const normal = nodes => nodes.filter(node => !node.disabled).map(node => {
    node.children = normal(node.children)
    if (node.type === 'GROUP') {
      node.vehicleCount = node.children.filter(child => child.type === 'VEHICLE').length
      node.totalVehicleCount = node.children.reduce((count, child) => count + (child.type === 'VEHICLE' ? 1 : child.totalVehicleCount), 0)
      node.label = buildGroupLabel(node.teamName, node)
    }
    return node
  })
  return sortGarageNodesWithUngroupedFirst(normal(roots))
}

export function createGarageTreeLoader() {
  let treeData = []

  async function fetchGarageChildrenRaw(parentId, page = 1) {
    const parent = findGarageNodeById(treeData, parentId)
    const start = (Math.max(1, Number(page) || 1) - 1) * GARAGE_CHILD_PAGE_SIZE
    return parent ? parent.children.slice(start, start + GARAGE_CHILD_PAGE_SIZE) : []
  }

  async function loadGarageChildren(parentId, expectedTotal = null) {
    const list = await fetchGarageChildrenRaw(parentId, 1)
    const normalized = await Promise.all(list.map((item) => normalizeGarageNode(item)))
    if (shouldShowGarageLoadMore(expectedTotal, list.length, normalized.length)) {
      normalized.push(createGarageLoadMoreNode(parentId, 2))
    }
    return normalized
  }

  async function normalizeGarageNode(item) {
    const raw = item && typeof item === 'object' ? item : {}
    const idRaw = raw.id != null ? raw.id : raw.teamId != null ? raw.teamId : raw.vehicleId
    const id = idRaw != null ? String(idRaw) : `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const type = raw.type != null ? String(raw.type).toUpperCase() : ''
    const isVehicle = type === 'VEHICLE' || raw.vehicleNo != null
    const name =
      raw.title != null
        ? String(raw.title)
        : isVehicle
          ? String(raw.vehicleNo || raw.vclN || id)
          : String(raw.teamName || raw.name || id)
    const label = isVehicle ? name : buildGroupLabel(name, raw)

    const node = {
      ...raw,
      id,
      label,
      displayName: name,
      type: isVehicle ? 'VEHICLE' : 'GROUP',
      children: []
    }

    if (isVehicle) {
      node.isLeaf = true
      return node
    }

    if (Array.isArray(raw.children) && raw.children.length) {
      node.children = await Promise.all(raw.children.map((c) => normalizeGarageNode(c)))
      return node
    }

    if (raw.hasChildren === true || raw.hasChildren === 1) {
      try {
        node.garageExpectedChildTotal = resolveGarageGroupExpectedChildTotal(raw)
        node.children = await loadGarageChildren(id, node.garageExpectedChildTotal)
      } catch (e) {
        node.children = []
      }
    }
    return node
  }

  async function loadGarageTreeData(query = '') {
    treeData = await loadNormalVehicleGarageTree(query)
    return treeData
  }

  async function handleGarageLoadMore(treeData, data) {
    const parentId = data && data.garageLoadMoreParentId
    const page = data && data.garageChildrenNextPage
    if (parentId == null || page == null) return treeData
    const p = Number(page)
    if (!Number.isFinite(p) || p < 2) return treeData

    const parentNode = findGarageNodeById(treeData, parentId)
    if (!parentNode) return treeData

    const prev = Array.isArray(parentNode.children) ? parentNode.children : []
    const withoutMore = prev.filter((c) => c && c.type !== GARAGE_NODE_LOAD_MORE)
    const list = await fetchGarageChildrenRaw(parentId, p)
    if (!list.length) {
      parentNode.children = withoutMore
      return [...treeData]
    }
    const newNormalized = await Promise.all(list.map((item) => normalizeGarageNode(item)))
    const seen = new Set(withoutMore.map((c) => String(c.id)))
    for (const nn of newNormalized) {
      if (nn && !seen.has(String(nn.id))) {
        withoutMore.push(nn)
        seen.add(String(nn.id))
      }
    }
    const merged = withoutMore
    const totalExp = parentNode.garageExpectedChildTotal
    if (shouldShowGarageLoadMore(totalExp, list.length, merged.length)) {
      merged.push(createGarageLoadMoreNode(parentId, p + 1))
    }
    parentNode.children = merged
    return [...treeData]
  }

  return {
    loadGarageTreeData,
    handleGarageLoadMore,
    normalizeGarageNode,
    fetchGarageChildrenRaw
  }
}

/** 车辆树叶子节点小车图标（render 函数用，颜色跟随系统主题） */
export function renderGarageVehicleTreeIcon(h, className = 'garage-tree-vehicle-icon') {
  return h(
    'span',
    {
      class: [className, 'layout-theme-vehicle-icon'],
      attrs: { 'aria-hidden': 'true' }
    },
    [
      h('svg', {
        attrs: {
          viewBox: '0 0 16 16',
          xmlns: 'http://www.w3.org/2000/svg',
          focusable: 'false'
        }
      }, [
        h('path', {
          attrs: {
            d: 'M1.5 9.5h13l-1.2-4.1A1 1 0 0 0 12.34 4H3.66a1 1 0 0 0-.94.66L1.5 9.5z',
            fill: 'currentColor'
          }
        }),
        h('rect', { attrs: { x: '4.5', y: '5.5', width: '2.5', height: '1.8', rx: '0.3', fill: '#fff', opacity: '0.85' } }),
        h('rect', { attrs: { x: '9', y: '5.5', width: '2.5', height: '1.8', rx: '0.3', fill: '#fff', opacity: '0.85' } }),
        h('circle', { attrs: { cx: '4.5', cy: '10.5', r: '1.4', fill: '#303133' } }),
        h('circle', { attrs: { cx: '11.5', cy: '10.5', r: '1.4', fill: '#303133' } }),
        h('circle', { attrs: { cx: '4.5', cy: '10.5', r: '0.55', fill: '#909399' } }),
        h('circle', { attrs: { cx: '11.5', cy: '10.5', r: '0.55', fill: '#909399' } })
      ])
    ]
  )
}
